import React from 'react';
import Footer from '../components/Footer';

const days = [
  {
    num: '29',
    weekday: 'Thursday',
    month: 'October 2026',
    events: [
      {
        time: '6:00 PM',
        name: 'Welcome Party',
        venue: 'Laplaya Beach Club',
        venueLink: 'https://maps.google.com/?q=9891+Gulf+Shore+Dr+Naples+FL+34108',
        address: '9891 Gulf Shore Dr, Naples, FL 34108',
        attire: 'Coastal Cocktail',
        notes: 'Join us for a relaxed evening on the beach to kick off the weekend. Cocktails and light appetizers will be served. Please note this event will be outdoors on grass — please plan footwear accordingly! Transportation will be provided to and from Naples Beach Club, and complimentary valet is available for those driving.',
        end: '8:30 PM',
      },
    ],
  },
  {
    num: '30',
    weekday: 'Friday',
    month: 'October 2026',
    events: [
      {
        time: '3:00 PM',
        name: 'Wedding Mass',
        venue: 'Saint William Catholic Church',
        venueLink: 'https://maps.google.com/?q=601+Seagate+Dr+Naples+FL+34108',
        address: '601 Seagate Dr., Naples, FL 34108',
        attire: 'Black Tie',
        notes: 'Transportation will be provided from the Naples Beach Club to Saint William before Mass. Please plan to arrive 15–20 minutes early. The ceremony will last approximately one hour.',
        end: '4:00 PM',
      },
      {
        time: null,
        name: 'Between Mass & Reception',
        subtle: true,
        venue: null,
        venueLink: null,
        address: null,
        attire: null,
        bulletNotes: [
          'Shuttles will run from Saint William to the Naples Beach Club.',
          'Grab a drink at one of the Four Seasons bars — Sunset Bar, HB\'s, or the Merchant Room.',
          'Cocktail hour begins at 5:30 PM on the Palm Lawn.',
        ],
        end: null,
      },
      {
        time: '5:30 PM',
        name: 'Cocktail Hour, Dinner & Dancing',
        venue: 'Naples Beach Club, A Four Seasons Resort',
        venueLink: 'https://maps.google.com/?q=801+Gulf+Shore+Blvd+N+Naples+FL+34102',
        address: '801 Gulf Shore Blvd N, Naples, FL 34102',
        attire: 'Black Tie',
        notes: 'Cocktail hour will be outdoors on grass — please plan footwear accordingly!',
        end: '10:30 PM',
      },
      {
        time: '10:30 PM',
        name: 'After Party',
        venue: 'The Wager',
        venueLink: 'https://www.fourseasons.com/naplesbeachclub/dining/restaurants/wager-sports-pub/',
        address: 'Naples Beach Club, A Four Seasons Resort — next door to the reception',
        attire: null,
        notes: "Keep the night going at The Wager, the resort's sports pub right next door to the reception — bowling lanes, putting on the green outside, late-night food, and plenty of games (pop-a-shot, pool, Golden Tee).",
        end: '12:30 AM',
      },
    ],
  },
];

/* Reusable green day-header bar (also used by the Wedding Party page) */
export function DayHeader({ day }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--green-dark), var(--green))',
      padding: '22px 40px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        fontFamily: 'var(--font-serif)',
        fontSize: '3rem',
        color: 'white',
        lineHeight: 1,
        opacity: 0.9,
      }}>{day.num}</div>
      <div>
        <div style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '1rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'white',
          marginBottom: '4px',
        }}>{day.weekday}</div>
        <div style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '1.15rem',
          color: 'white',
          fontWeight: 400,
        }}>{day.month}</div>
      </div>
    </div>
  );
}

export default function Schedule() {
  return (
    <div className="site-wrapper">
      {/* Full-page photo + cream veil */}
      <div className="rsvp-bg-photo" style={{ backgroundImage: "url('/Saint William.png')" }} />
      <div className="rsvp-bg-overlay" />

      <div className="page-content" style={{ position: 'relative', zIndex: 2 }}>
        {/* Card: alternating green day-bars + white event sections */}
        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '40px 40px 80px' }}>
          <h2 style={{
            fontFamily: 'var(--font-script)',
            fontSize: '60px',
            fontWeight: 400,
            color: 'var(--green-dark)',
            textAlign: 'center',
            marginBottom: '8px',
            lineHeight: 1.2,
          }}>The Weekend Schedule</h2>
          <p style={{ textAlign: 'center', marginBottom: '24px', fontWeight: 600, fontSize: '1rem', color: 'grey', fontFamily: 'var(--font-sans)' }}>
            Everything you need to know for the wedding weekend.
          </p>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid rgba(45, 80, 22, 0.1)',
          }}>
            {days.map((day) => (
              <React.Fragment key={day.num}>
                {/* Green day-header bar */}
                <DayHeader day={day} />

                {/* Events for this day */}
                <div className="schedule-timeline" style={{ margin: 0, maxWidth: 'none', padding: '36px 40px' }}>
                  <div className="schedule-day" style={{ marginBottom: 0 }}>
                    <div className="schedule-events">
                      {day.events.map((event) => (
                        <div className={`schedule-event${event.subtle ? ' schedule-event--subtle' : ''}`} key={event.name}>
                          <div className="schedule-time">
                            {event.time && <>{event.time}</>}
                            {event.time && event.end && <><br /><span>– {event.end}</span></>}
                          </div>

                          <div className="schedule-event-body">
                            <div className="schedule-event-name" style={event.subtle ? { fontSize: '0.85rem', fontFamily: 'var(--font-sans)', fontWeight: 500, color: 'var(--text-light)', letterSpacing: '0.06em', textTransform: 'uppercase' } : undefined}>{event.name}</div>

                            {event.venue && (
                            <div className="schedule-event-detail">
                              <span>📍</span>
                              <span>
                                <a href={event.venueLink} target="_blank" rel="noopener noreferrer">
                                  {event.venue}
                                </a>
                              </span>
                            </div>
                            )}

                            {event.address && (
                            <div className="schedule-event-detail">
                              <span style={{ opacity: 0 }}>📍</span>
                              <span>{event.address}</span>
                            </div>
                            )}

                            {event.notes && (
                              <div className="schedule-event-detail" style={{ marginTop: '8px' }}>
                                <span style={{ opacity: 0 }}>📍</span>
                                <span style={{ fontStyle: 'italic', color: 'var(--text-light)' }}>{event.notes}</span>
                              </div>
                            )}

                            {event.bulletNotes && (
                              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                                {event.bulletNotes.map((note, i) => (
                                  <li key={i} style={{ fontStyle: 'italic', color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '6px', fontFamily: 'var(--font-sans)' }}>{note}</li>
                                ))}
                              </ul>
                            )}

                            {event.attire && (
                              <div className="schedule-attire-badge">
                                Attire: {event.attire}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}


          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
